import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useUserProfile, useUpdateProfile } from '@/hooks/useUserProfile'
import { apiClient } from '@/api/client'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { User, Edit2, Mail, Save, X, CheckCircle, AlertCircle, Upload, Trash2 } from 'lucide-react'

export function Profile() {
  const { user, getAccessTokenSilently } = useAuth0()
  const { profile, isLoading, refetch } = useUserProfile()
  const updateProfile = useUpdateProfile()

  const [isEditingNickname, setIsEditingNickname] = useState(false)
  const [nickname, setNickname] = useState('')
  const [isEditingPicture, setIsEditingPicture] = useState(false)
  const [pictureUrl, setPictureUrl] = useState('')
  const [uploadingPicture, setUploadingPicture] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleEditNickname = () => {
    setNickname(profile?.nickname || '')
    setIsEditingNickname(true)
    setError('')
    setSuccess('')
  }

  const handleCancelEdit = () => {
    setIsEditingNickname(false)
    setNickname('')
    setError('')
  }

  const handleEditPicture = () => {
    setPictureUrl(profile?.profile_picture_url || '')
    setIsEditingPicture(true)
    setError('')
    setSuccess('')
  }

  const handleCancelPictureEdit = () => {
    setIsEditingPicture(false)
    setPictureUrl('')
    setError('')
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setError('')
    setUploadingPicture(true)

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })

      const formData = new FormData()
      formData.append('image', file)

      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.post('/upload/profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      // Use current origin in production, localhost in development
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      const baseUrl = isProduction ? window.location.origin : (import.meta.env.VITE_API_URL || 'http://localhost:3002')
      const uploadedUrl = `${baseUrl}${response.data.url}`
      
      await updateProfile.mutateAsync({ profile_picture_url: uploadedUrl })
      setSuccess('Profile picture uploaded successfully!')
      setIsEditingPicture(false)
      refetch()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload profile picture')
    } finally {
      setUploadingPicture(false)
    }
  }

  const handleSavePicture = async () => {
    setError('')
    setSuccess('')

    try {
      await updateProfile.mutateAsync({ profile_picture_url: pictureUrl.trim() || null as any })
      setSuccess('Profile picture updated successfully!')
      setIsEditingPicture(false)
      refetch()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile picture')
    }
  }

  const handleDeletePicture = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return
    }

    setError('')

    try {
      await updateProfile.mutateAsync({ profile_picture_url: null as any })
      setSuccess('Profile picture deleted successfully!')
      refetch()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete profile picture')
    }
  }

  const handleSaveNickname = async () => {
    setError('')
    setSuccess('')

    // Validate nickname
    if (!nickname.trim()) {
      setError('Nickname is required')
      return
    }

    if (nickname.trim().length < 2) {
      setError('Nickname must be at least 2 characters')
      return
    }

    if (nickname.trim().length > 50) {
      setError('Nickname must be less than 50 characters')
      return
    }

    if (!/^[a-zA-Z0-9\s_-]+$/.test(nickname.trim())) {
      setError('Nickname can only contain letters, numbers, spaces, underscores, and hyphens')
      return
    }

    try {
      await updateProfile.mutateAsync({ nickname: nickname.trim() })
      setSuccess('Nickname updated successfully!')
      setIsEditingNickname(false)
      refetch()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update nickname')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account settings
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Profile Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-shrink-0">
              {profile?.profile_picture_url ? (
                <img 
                  src={profile.profile_picture_url} 
                  alt={profile.nickname || profile.display_name}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="h-8 w-8 text-blue-600" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900">
                {profile?.nickname || profile?.display_name || user?.name || 'User'}
              </h3>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Mail className="h-4 w-4" />
                <span>{profile?.email || user?.email || 'No email'}</span>
              </div>
            </div>
          </div>

          {/* Nickname Section */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Nickname
              </label>
              {!isEditingNickname && (
                <button
                  onClick={handleEditNickname}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Edit2 className="h-3 w-3 mr-1" />
                  Edit
                </button>
              )}
            </div>

            {isEditingNickname ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="Enter your nickname"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  disabled={updateProfile.isPending}
                />
                <p className="text-xs text-gray-500">
                  2-50 characters. Letters, numbers, spaces, underscores, and hyphens only.
                </p>

                {error && (
                  <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={handleSaveNickname}
                    disabled={updateProfile.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {updateProfile.isPending ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={updateProfile.isPending}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-900">
                {profile?.nickname || <span className="text-gray-400 italic">No nickname set</span>}
              </p>
            )}
          </div>

          {/* Profile Picture Section */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Profile Picture
              </label>
              {!isEditingPicture && (
                <button
                  onClick={handleEditPicture}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Edit2 className="h-3 w-3 mr-1" />
                  Edit
                </button>
              )}
            </div>

            {isEditingPicture ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Upload Picture
                  </label>
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="picture-upload"
                      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                        uploadingPicture
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 cursor-pointer'
                      }`}
                    >
                      {uploadingPicture ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Choose File
                        </>
                      )}
                    </label>
                    <input
                      id="picture-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploadingPicture}
                      className="hidden"
                    />
                    <span className="text-xs text-gray-500">
                      JPG, PNG, GIF, or WEBP (max 5MB)
                    </span>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-white text-gray-500">or paste URL</span>
                  </div>
                </div>
                
                <input
                  type="url"
                  value={pictureUrl}
                  onChange={(e) => setPictureUrl(e.target.value)}
                  placeholder="Enter image URL (e.g., https://example.com/photo.jpg)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  disabled={updateProfile.isPending || uploadingPicture}
                />
                <p className="text-xs text-gray-500">
                  Upload a file or paste a direct link to your profile picture. Leave empty to remove.
                </p>

                {error && (
                  <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={handleSavePicture}
                    disabled={updateProfile.isPending || uploadingPicture}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {updateProfile.isPending ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancelPictureEdit}
                    disabled={updateProfile.isPending || uploadingPicture}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {profile?.profile_picture_url ? (
                    <img 
                      src={profile.profile_picture_url} 
                      alt="Profile" 
                      className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                      <User className="h-10 w-10 text-gray-400" />
                    </div>
                  )}
                  <p className="text-sm text-gray-500">
                    {profile?.profile_picture_url ? 'Current profile picture' : 'No profile picture set'}
                  </p>
                </div>
                {profile?.profile_picture_url && (
                  <button
                    onClick={handleDeletePicture}
                    disabled={updateProfile.isPending}
                    className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Picture
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Display Name Section */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name
            </label>
            <p className="text-sm text-gray-900">{profile?.display_name}</p>
            <p className="text-xs text-gray-500 mt-1">This is set from your Auth0 profile</p>
          </div>

          {/* Account Info */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Information
            </label>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-gray-500">Member Since</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Onboarding Complete</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {profile?.has_completed_onboarding ? (
                    <span className="text-green-600">Yes</span>
                  ) : (
                    <span className="text-yellow-600">No</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}
